import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Menu, 
  X, 
  Home, 
  Wrench, 
  ClipboardCheck, 
  BookOpen, 
  MoreHorizontal,
  MessageSquare,
  Building2,
  UserCog,
  FileStack,
  Monitor,
  ChevronDown
} from 'lucide-react';
import { Button } from './ui/button';

const navLinks = [
  { name: 'Startseite', path: '/', icon: Home },
  { name: 'Leistungen', path: '/leistungen', icon: Wrench },
  { name: 'Ratgeber', path: '/ratgeber', icon: BookOpen },
];

const dropdownLinks = [
  { name: 'Bildungsträger gründen', path: '/bildungstraeger-gruenden', icon: Building2 },
  { name: 'Internes Audit', path: '/internes-audit', icon: ClipboardCheck },
  { name: 'Externer QM-Beauftragter', path: '/externer-qm-beauftragter', icon: UserCog },
  { name: 'Musterunterlagen', path: '/musterunterlagen', icon: FileStack },
  { name: 'Digitale Handbuch-Software', path: '/handbuch-software', icon: Monitor },
];

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    // Check initial scroll position
    handleScroll();

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on route change
  useEffect(() => {
    setIsDropdownOpen(false);
    setIsOpen(false);
  }, [location]);

  return (
    <nav 
      data-testid="navbar" 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-[#050505]/90 backdrop-blur-xl border-b border-white/10' 
          : 'bg-transparent border-b border-white/10'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo with tagline */}
          <Link to="/" className="flex items-center gap-4" data-testid="navbar-logo">
            <div className="flex items-center">
              <span className="text-2xl font-heading font-bold text-white tracking-tight">MEHR</span>
              <span className="text-2xl font-heading font-bold text-primary tracking-tight">AZAV</span>
            </div>
            <span className="hidden xl:block text-sm text-muted-foreground border-l border-white/10 pl-4">
              Beratung, Audits,<br />QM-Systeme & Musterunterlagen.
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.path;
              const Icon = link.icon;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
                  className="relative group px-4 py-2"
                >
                  <div className={`flex items-center gap-2 text-sm font-medium transition-colors group-hover:text-white ${
                    isActive ? 'text-white' : 'text-muted-foreground'
                  }`}>
                    <Icon size={16} className="text-primary" />
                    <span>{link.name}</span>
                  </div>
                  {/* Underline */}
                  <span className={`absolute bottom-0 left-4 right-4 h-[2px] bg-primary transition-transform origin-left ${
                    isActive ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                  }`} />
                </Link>
              );
            })}

            {/* More Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-muted-foreground hover:text-white transition-colors"
                data-testid="nav-more-dropdown"
              >
                <MoreHorizontal size={16} className="text-primary" />
                <span>Mehr</span>
                <ChevronDown 
                  size={14} 
                  className={`transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} 
                />
              </button>

              {/* Dropdown Menu */}
              <AnimatePresence>
                {isDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full right-0 mt-2 w-64 bg-[#0A0A0A] border border-white/10 rounded-xl shadow-xl overflow-hidden"
                    data-testid="dropdown-menu"
                  >
                    <div className="py-2">
                      {dropdownLinks.map((item, index) => {
                        const Icon = item.icon;
                        return (
                          <Link
                            key={index}
                            to={item.path}
                            className="flex items-center gap-3 px-4 py-3 text-sm text-muted-foreground hover:text-white hover:bg-white/5 transition-colors"
                            data-testid={`dropdown-item-${index}`}
                          >
                            <Icon size={18} className="text-primary" />
                            <span>{item.name}</span>
                          </Link>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            
            {/* CTA Button */}
            <Link to="/kontakt" className="ml-4">
              <Button 
                data-testid="nav-cta-button"
                className="bg-primary hover:bg-[#05b873] text-black font-semibold rounded-lg px-6 py-5 text-sm transition-all duration-300 flex items-center gap-2"
              >
                <MessageSquare size={16} />
                Anfrage stellen
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-button"
            className="lg:hidden text-white p-2"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-[#050505]/95 backdrop-blur-xl border-b border-white/5"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-4 space-y-1">
              {navLinks.map((link) => {
                const isActive = location.pathname === link.path;
                const Icon = link.icon;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid={`mobile-nav-link-${link.path.replace('/', '') || 'home'}`}
                    onClick={() => setIsOpen(false)}
                    className={`flex items-center gap-3 py-3 text-base font-medium transition-colors hover:text-white ${
                      isActive ? 'text-white' : 'text-muted-foreground'
                    }`}
                  >
                    <Icon size={18} className="text-primary" />
                    {link.name}
                  </Link>
                );
              })}

              {/* Mobile Dropdown Items */}
              <div className="pt-2 mt-2 border-t border-white/10">
                <p className="flex items-center gap-2 py-2 text-xs uppercase tracking-wider text-muted-foreground">
                  <MoreHorizontal size={14} className="text-primary" />
                  Mehr
                </p>
                {dropdownLinks.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={index}
                      to={item.path}
                      onClick={() => setIsOpen(false)}
                      className="flex items-center gap-3 py-3 pl-6 text-base text-muted-foreground hover:text-white transition-colors"
                    >
                      <Icon size={18} className="text-primary" />
                      {item.name}
                    </Link>
                  );
                })}
              </div>

              <div className="pt-4">
                <Link to="/kontakt" onClick={() => setIsOpen(false)}>
                  <Button 
                    data-testid="mobile-nav-cta-button"
                    className="w-full bg-primary hover:bg-[#05b873] text-black font-semibold rounded-lg px-6 py-4 text-base flex items-center justify-center gap-2"
                  >
                    <MessageSquare size={18} />
                    Anfrage stellen
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};
